import { useState, useEffect } from 'react';
import axios from 'axios';
import { Bot, BookOpen, Trash2, Plus, Check, MessageSquare, Search } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminChatbot = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('logs');
  const [chatLogs, setChatLogs] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [newQA, setNewQA] = useState({ question: '', answer: '', category: 'general' });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchChatLogs();
    fetchKnowledge();
  }, []);

  const fetchChatLogs = async () => {
    try {
      const response = await axios.get(`${API}/chatbot/logs`, {
        headers: getAuthHeader(),
      });
      setChatLogs(response.data);
    } catch (error) {
      console.error('Error fetching chat logs:', error);
    }
  };

  const fetchKnowledge = async () => {
    try {
      const response = await axios.get(`${API}/chatbot/knowledge`, {
        headers: getAuthHeader(),
      });
      setKnowledge(response.data);
    } catch (error) {
      console.error('Error fetching knowledge:', error);
    }
  };

  const approveLog = async (log) => {
    try {
      await axios.post(`${API}/chatbot/logs/${log.conversation_id}/approve`, {}, {
        headers: getAuthHeader(),
      });
      toast.success('Ajouté à la base de connaissances!');
      fetchKnowledge();
    } catch (error) {
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const addKnowledge = async () => {
    if (!newQA.question || !newQA.answer) {
      toast.error('Veuillez remplir la question et la réponse');
      return;
    }
    try {
      await axios.post(`${API}/chatbot/learn`, newQA, {
        headers: getAuthHeader(),
      });
      toast.success('Connaissance ajoutée!');
      setNewQA({ question: '', answer: '', category: 'general' });
      fetchKnowledge();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const deleteKnowledge = async (id) => {
    try {
      await axios.delete(`${API}/chatbot/knowledge/${id}`, {
        headers: getAuthHeader(),
      });
      toast.success('Connaissance supprimée');
      fetchKnowledge();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredLogs = chatLogs.filter(log => 
    log.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.ai_response?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredKnowledge = knowledge.filter(k =>
    k.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    k.answer?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="admin-chatbot">
        {/* Header */}
        <div>
          <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Bot className="h-5 w-5 text-purple-600" />
            Assistant IA - Apprentissage
          </h1>
          <p className="text-slate-500 text-xs">Gérer les connaissances et voir les conversations du chatbot</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 pb-2">
          <Button
            variant={activeTab === 'logs' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('logs')}
            className={`text-xs h-8 ${activeTab === 'logs' ? 'bg-purple-600' : 'text-slate-600'}`}
            size="sm"
          >
            <MessageSquare className="h-3 w-3 mr-1" />
            Conversations ({chatLogs.length})
          </Button>
          <Button
            variant={activeTab === 'knowledge' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('knowledge')}
            className={`text-xs h-8 ${activeTab === 'knowledge' ? 'bg-purple-600' : 'text-slate-600'}`}
            size="sm"
          >
            <BookOpen className="h-3 w-3 mr-1" />
            Base de connaissances ({knowledge.length})
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher..."
            className="pl-8 bg-white border-slate-200 text-xs h-8"
          />
        </div>

        {/* Chat Logs Tab */}
        {activeTab === 'logs' && (
          <div className="space-y-3">
            {filteredLogs.length === 0 ? (
              <p className="text-slate-400 text-center py-6 text-xs">Aucune conversation enregistrée</p>
            ) : (
              filteredLogs.map((log, index) => (
                <div key={index} className="bg-white rounded-lg p-3 border border-slate-200">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="text-[10px] text-slate-500">
                        {log.visitor_name} • {new Date(log.created_at).toLocaleString('fr-CA')}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => approveLog(log)}
                      className="bg-green-600 hover:bg-green-700 text-xs h-6"
                    >
                      <Check className="h-3 w-3 mr-1" />
                      Approuver
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-2">
                      <span className="text-[10px] text-blue-600 font-medium">Question client:</span>
                      <p className="text-slate-800 mt-0.5 text-xs">{log.question}</p>
                    </div>
                    <div className="bg-purple-50 border border-purple-100 rounded-lg p-2">
                      <span className="text-[10px] text-purple-600 font-medium">Réponse IA:</span>
                      <p className="text-slate-800 mt-0.5 whitespace-pre-wrap text-xs">{log.ai_response}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Knowledge Tab */}
        {activeTab === 'knowledge' && (
          <div className="space-y-4">
            {/* Add new Q&A */}
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <h3 className="text-slate-800 font-semibold mb-2 text-xs flex items-center gap-1">
                <Plus className="h-3 w-3" />
                Ajouter une nouvelle connaissance
              </h3>
              <div className="space-y-2">
                <div>
                  <Label className="text-slate-600 text-[10px]">Question fréquente</Label>
                  <Input
                    value={newQA.question}
                    onChange={(e) => setNewQA(prev => ({ ...prev, question: e.target.value }))}
                    placeholder="Ex: Offrez-vous le financement?"
                    className="bg-white border-slate-200 mt-1 text-xs h-7"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 text-[10px]">Réponse souhaitée</Label>
                  <Textarea
                    value={newQA.answer}
                    onChange={(e) => setNewQA(prev => ({ ...prev, answer: e.target.value }))}
                    placeholder="Ex: Oui! Nous offrons plusieurs options de financement..."
                    rows={2}
                    className="bg-white border-slate-200 mt-1 text-xs"
                  />
                </div>
                <Button onClick={addKnowledge} className="bg-purple-600 hover:bg-purple-700 text-xs h-7" size="sm">
                  <Plus className="h-3 w-3 mr-1" />
                  Ajouter
                </Button>
              </div>
            </div>

            {/* Knowledge list */}
            <div className="space-y-2">
              {filteredKnowledge.length === 0 ? (
                <p className="text-slate-400 text-center py-6 text-xs">Aucune connaissance enregistrée</p>
              ) : (
                filteredKnowledge.map((k) => (
                  <div key={k.id} className="bg-white rounded-lg p-3 border border-slate-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="text-blue-600 font-medium mb-1 text-xs">Q: {k.question}</div>
                        <div className="text-slate-700 whitespace-pre-wrap text-xs">R: {k.answer}</div>
                        <div className="mt-2 flex gap-1.5">
                          <span className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                            {k.category}
                          </span>
                          <span className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                            Utilisé {k.use_count || 0} fois
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteKnowledge(k.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 h-7 w-7"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminChatbot;
